import {
  deleteAnnouncement,
  getAnnouncementById,
  updateAnnouncementStatus,
} from '@/api/announcements';
import { AnnouncementDetailData } from '@/api/announcements/types';
import { Constants } from '@/constants/announcements';
import useErrorMessage from '@/hooks/useErrorMessage';
import useSnackbar from '@/hooks/useSnackbar';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  deleteMockAnnouncement,
  getActiveMockAnnouncement,
  getMockAnnouncement,
  updateMockAnnouncementStatus,
  USE_MOCK,
} from '../mocks';
import { getYoutubeThumbnail } from '../videoUtils';

interface UseDetailsHook {
  announcement: AnnouncementDetailData | undefined;
  isLoading: boolean;
  openActivate: boolean;
  openDeactivate: boolean;
  openDelete: boolean;
  openPreview: boolean;
  activeWarning: string | undefined;
  setOpenActivate: (open: boolean) => void;
  setOpenDeactivate: (open: boolean) => void;
  setOpenDelete: (open: boolean) => void;
  setOpenPreview: (open: boolean) => void;
  handleToggleActive: () => void;
  handleConfirmActivate: () => Promise<void>;
  handleConfirmDeactivate: () => Promise<void>;
  handleConfirmDelete: () => Promise<void>;
  handleBack: () => void;
  handleEdit: () => void;
}

export default function useDetails(): UseDetailsHook {
  const [announcement, setAnnouncement] = useState<AnnouncementDetailData>();
  const [isLoading, setIsLoading] = useState(true);
  const [openActivate, setOpenActivate] = useState(false);
  const [openDeactivate, setOpenDeactivate] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);

  const { id } = useParams();
  const announcementId = Number(id);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();
  const navigate = useNavigate();

  const fetchAnnouncement = useCallback(async () => {
    setIsLoading(true);
    try {
      if (USE_MOCK) {
        const mock = getMockAnnouncement(announcementId);
        if (!mock) throw new Error(Constants.notFound);
        setAnnouncement(mock);
        return;
      }

      const response = await getAnnouncementById(announcementId);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (!response.data) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      setAnnouncement(response.data);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
      navigate('/announcements');
    } finally {
      setIsLoading(false);
    }
  }, [announcementId, handleSnackbar, getErrorMessage, navigate]);

  useEffect(() => {
    fetchAnnouncement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcementId]);

  const currentActive = USE_MOCK ? getActiveMockAnnouncement() : undefined;
  const activeWarning =
    currentActive && currentActive.id !== announcementId
      ? Constants.activeWillBeDeactivated(currentActive.title)
      : undefined;

  const handleToggleActive = (): void => {
    if (!announcement) return;
    if (announcement.status === 'active') {
      setOpenDeactivate(true);
    } else {
      setOpenActivate(true);
    }
  };

  const handleConfirmActivate = async (): Promise<void> => {
    setOpenActivate(false);

    const hasInvalidVideo = announcement?.cards.some(
      (card) => card.video_url && !getYoutubeThumbnail(card.video_url)
    );
    if (hasInvalidVideo) {
      handleSnackbar(Constants.invalidVideoUrl, true);
      return;
    }

    try {
      if (USE_MOCK) {
        updateMockAnnouncementStatus(announcementId, 'active');
      } else {
        const response = await updateAnnouncementStatus(
          announcementId,
          'active'
        );
        if (response.detail.description) {
          throw new Error(response.detail.description);
        }
      }
      handleSnackbar(Constants.activated, false);
      await fetchAnnouncement();
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  const handleConfirmDeactivate = async (): Promise<void> => {
    setOpenDeactivate(false);
    try {
      if (USE_MOCK) {
        updateMockAnnouncementStatus(announcementId, 'inactive');
      } else {
        const response = await updateAnnouncementStatus(
          announcementId,
          'inactive'
        );
        if (response.detail.description) {
          throw new Error(response.detail.description);
        }
      }
      handleSnackbar(Constants.deactivated, false);
      await fetchAnnouncement();
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  const handleConfirmDelete = async (): Promise<void> => {
    setOpenDelete(false);
    try {
      if (USE_MOCK) {
        deleteMockAnnouncement(announcementId);
      } else {
        const response = await deleteAnnouncement(announcementId);
        if (response.detail.description) {
          throw new Error(response.detail.description);
        }
      }
      handleSnackbar(Constants.deleted, false);
      navigate('/announcements');
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  const handleBack = (): void => {
    navigate('/announcements');
  };

  const handleEdit = (): void => {
    navigate(`/announcements/${announcementId}/edit`);
  };

  return {
    announcement,
    isLoading,
    openActivate,
    openDeactivate,
    openDelete,
    openPreview,
    activeWarning,
    setOpenActivate,
    setOpenDeactivate,
    setOpenDelete,
    setOpenPreview,
    handleToggleActive,
    handleConfirmActivate,
    handleConfirmDeactivate,
    handleConfirmDelete,
    handleBack,
    handleEdit,
  };
}
